import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertCircle, ArrowLeft, Play, RefreshCw, Square } from 'lucide-react';
import { apiService } from '../services/api';

const TestRunner = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const taskId = searchParams.get('taskId');
  
  const [status, setStatus] = useState('starting');
  const [progress, setProgress] = useState(null);
  const [testRunId, setTestRunId] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stopping, setStopping] = useState(false);
  const cancelledRef = React.useRef(false);

  useEffect(() => {
    if (taskId) {
      pollTaskStatus();
    } else {
      // No task ID, redirect to test runs
      navigate('/test-runs');
    }
  }, [taskId, navigate]);

  const addLog = (message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message
    }]);
  };

  const pollTaskStatus = async () => {
    if (cancelledRef.current) return;
    try {
      const response = await apiService.getTaskStatus(taskId);
      const taskStatus = response.data.status;
      const result = response.data.result;

      if (cancelledRef.current) return;

      setStatus(taskStatus.toLowerCase());

      if (taskStatus === 'SUCCESS') {
        setProgress(result);
        if (result?.test_run_id) {
          setTestRunId(result.test_run_id);
        }
        addLog('✅ Test run completed successfully');
        
      } else if (taskStatus === 'FAILURE') {
        setError(result?.error || 'Test execution failed');
        addLog('❌ Test run failed');

      } else if (taskStatus === 'REVOKED') {
        setStatus('cancelled');
        addLog('🛑 Test run was cancelled');
        setStopping(false);
        
      } else if (taskStatus === 'PROGRESS') {
        setProgress(result);
        if (result?.current_test) {
          const icon = result.current_test_status === 'passed' ? '✅' :
                       result.current_test_status === 'failed' ? '❌' :
                       result.current_test_status === 'skipped' ? '⏭️' : '🔄';
          addLog(`${icon} ${result.current_test}`);
          if (result.completed) {
            addLog(`   Progress: ${result.completed}/${result.total_tests || '?'} — ${result.passed_tests} passed, ${result.failed_tests} failed`);
          }
        } else if (result?.status) {
          addLog(`🔄 ${result.status}`);
        }
        if (result?.test_run_id && !testRunId) {
          setTestRunId(result.test_run_id);
        }
        // Continue polling
        setTimeout(pollTaskStatus, 2000);
        
      } else {
        // Task is still pending
        addLog('⏳ Test run is starting...');
        setTimeout(pollTaskStatus, 2000);
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setStatus('error');
      addLog('❌ Error checking test status');
    }
  };

  const handleStop = async () => {
    if (!testRunId || stopping) return;
    setStopping(true);
    try {
      await apiService.cancelTestRun(testRunId);
      cancelledRef.current = true;
      setStatus('cancelled');
      setStopping(false);
      addLog('🛑 Test run cancelled');
    } catch (err) {
      addLog('⚠️ Failed to stop test run');
      setStopping(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-8 w-8 text-success-500" />;
      case 'failure':
      case 'error':
        return <XCircle className="h-8 w-8 text-error-500" />;
      case 'cancelled':
        return <Square className="h-8 w-8 text-gray-500" />;
      case 'progress':
        return <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-8 w-8 text-warning-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'success':
        return 'Tests Completed Successfully';
      case 'failure':
        return 'Tests Failed';
      case 'error':
        return 'Execution Error';
      case 'cancelled':
        return 'Test Run Cancelled';
      case 'progress':
        return 'Running Tests...';
      default:
        return 'Starting Tests...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-success-600 bg-success-50 border-success-200';
      case 'failure':
      case 'error':
        return 'text-error-600 bg-error-50 border-error-200';
      case 'cancelled':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-warning-600 bg-warning-50 border-warning-200';
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/test-runs')}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to test runs
        </button>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Status Header */}
        <div className={`rounded-lg p-6 border-2 ${getStatusColor()}`}>
          <div className="flex items-center">
            {getStatusIcon()}
            <div className="ml-4">
              <h1 className="text-2xl font-semibold">{getStatusText()}</h1>
              <p className="text-sm opacity-75 mt-1">
                Task ID: {taskId}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Information */}
        {progress && (
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Test Progress</h3>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {progress.total_tests || 0}
                  </div>
                  <div className="text-sm text-gray-500">Total Tests</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-success-600">
                    {progress.passed_tests || 0}
                  </div>
                  <div className="text-sm text-gray-500">Passed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-error-600">
                    {progress.failed_tests || 0}
                  </div>
                  <div className="text-sm text-gray-500">Failed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">
                    {progress.duration ? `${(progress.duration / 1000).toFixed(1)}s` : '-'}
                  </div>
                  <div className="text-sm text-gray-500">Duration</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-6 bg-error-50 border border-error-200 rounded-md p-4">
            <div className="flex">
              <XCircle className="h-5 w-5 text-error-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-error-800">
                  Execution Error
                </h3>
                <div className="mt-2 text-sm text-error-700">
                  <pre className="whitespace-pre-wrap">{error}</pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Log */}
        <div className="mt-6 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Activity Log</h3>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map((log, index) => (
                <div key={index} className="flex items-start text-sm">
                  <span className="text-gray-500 mr-3 font-mono">
                    {log.timestamp}
                  </span>
                  <span className="text-gray-700">{log.message}</span>
                </div>
              ))}
              {logs.length === 0 && (
                <div className="text-gray-500 text-center py-4">
                  Waiting for test execution to begin...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-center space-x-4">
          {status === 'progress' && testRunId && (
            <button
              onClick={handleStop}
              disabled={stopping}
              className="bg-error-600 hover:bg-error-700 disabled:opacity-50 text-white px-6 py-2 rounded-md text-sm font-medium inline-flex items-center"
            >
              <Square className="h-4 w-4 mr-2" />
              {stopping ? 'Stopping...' : 'Stop Run'}
            </button>
          )}

          {status === 'success' && testRunId && (
            <button
              onClick={() => navigate(`/test-runs/${testRunId}`)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md text-sm font-medium inline-flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              View Detailed Results
            </button>
          )}
          
          {status === 'cancelled' && testRunId && (
            <button
              onClick={() => navigate(`/test-runs/${testRunId}`)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md text-sm font-medium inline-flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              View Partial Results
            </button>
          )}

          {(status === 'failure' || status === 'error') && (
            <button
              onClick={() => window.location.reload()}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md text-sm font-medium inline-flex items-center"
            >
              <Play className="h-4 w-4 mr-2" />
              Try Again
            </button>
          )}
          
          <button
            onClick={() => navigate('/test-runs')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md text-sm font-medium"
          >
            View All Test Runs
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestRunner;